/**
 * Game-level status banners shared by the setup view and the live dashboard:
 * the checkpoint hold, the Discord recovery prompt, and the two event logs.
 *
 * Grouped in one module because each is a small presentational panel over data
 * the caller has already derived — splitting them into four files would spread a
 * single idea across the folder without making any of them easier to find.
 */

import {
  TRL_SABOTAGE_DEFINITIONS,
  describeTrlTreasureItem,
  type TrlDisruptionRecord,
  type TrlSabotageEffectRecord,
} from '@swtorbit/shared-socket-events';
import { getTrlSabotageIconUrl } from '@swtorbit/shared-assets';

export interface CheckpointStatus {
  /** The checkpoint round teams are held at */
  level: number;
  heldTeamNames: string[];
  /** Teams that haven't reached the checkpoint round yet */
  waitingOnTeamNames: string[];
  arrivedCount: number;
  totalCount: number;
}

/**
 * Shown while teams are held at a checkpoint: who is holding, who the field is
 * waiting on, and the escape hatch to release the barrier early (e.g. a team
 * has dropped out and would otherwise stall every other team indefinitely).
 */
export function CheckpointPanel({
  status,
  onRelease,
}: {
  status: CheckpointStatus;
  onRelease: () => void;
}) {
  return (
    <div className="bg-space bg-o-50 border-md border-amber rounded-base p-sm d-flex flex-column gap-xs">
      <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
        <h3 className="m-0 text-amber">
          ⚑ Checkpoint — Round {status.level}: {status.arrivedCount} of {status.totalCount} teams arrived
        </h3>
        <button className="btn btn--amber btn--sm" onClick={onRelease}>
          Release checkpoint now
        </button>
      </div>
      <span className="fs-sm">
        <span className="text-slate">Holding: </span>
        {status.heldTeamNames.join(', ')}
      </span>
      <span className="fs-sm">
        <span className="text-slate">Still to arrive: </span>
        {status.waitingOnTeamNames.join(', ')}
      </span>
      <p className="field__hint fs-xs m-0">
        Held teams continue automatically once every team clears this round. Releasing now lets them
        continue without waiting; a team still to arrive passes straight through when it clears the checkpoint.
      </p>
    </div>
  );
}

/**
 * Recovery prompt shown when the game is running with Discord linked but some
 * teams have no voice channel.
 *
 * Channels are created once, at Start Game. If the bot was offline or the guild
 * unreachable at that moment every retry was exhausted and there is no further
 * automatic attempt, so without this the teams stay channel-less for the whole
 * game. Pressing re-sync re-runs the creation pass; teams that already have a
 * channel are untouched.
 */
export function DiscordResyncPanel({
  missingCount,
  totalCount,
  isResyncing,
  message,
  onResync,
}: {
  missingCount: number;
  totalCount: number;
  isResyncing: boolean;
  message: string | null;
  onResync: () => void;
}) {
  return (
    <div className="bg-space bg-o-50 border-md border-amber rounded-base p-sm d-flex flex-column gap-xs">
      <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
        <h3 className="m-0 text-amber">
          ⚠️ Discord — {missingCount} of {totalCount} team{totalCount === 1 ? '' : 's'} without a voice channel
        </h3>
        <button className="btn btn--amber btn--sm" onClick={onResync} disabled={isResyncing}>
          {isResyncing ? 'Re-syncing…' : 'Re-sync Discord channels'}
        </button>
      </div>
      <p className="field__hint fs-xs m-0">
        Team channels and roles are created when the game starts. These teams missed out — usually
        because the bot was offline or the server was unreachable at that moment. Re-syncing retries
        the ones that are missing and leaves existing channels alone.
      </p>
      {message && <span className="fs-sm">{message}</span>}
    </div>
  );
}

/**
 * Every disruption this game, newest first: who blocked whom, on which planet,
 * and how (or whether) the block was cleared.
 */
export function DisruptionLogPanel({
  records,
  teamNameById,
  nodeLabelById,
}: {
  records: TrlDisruptionRecord[];
  teamNameById: (teamId: string) => string;
  nodeLabelById: (nodeId: string) => string;
}) {
  const sorted = [...records].sort((a, b) => b.disruptedAt - a.disruptedAt);
  const time = (ms: number) => new Date(ms).toLocaleTimeString();

  return (
    <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs">
      <h3 className="m-0">🧨 Disruption log</h3>
      {sorted.map((r) => (
        <div
          key={r.id}
          className="d-flex align-items-center justify-content-between gap-xs flex-wrap p-2xs rounded-base bg-space bg-o-50 fs-sm"
        >
          <span>
            <span className="text-slate">{time(r.disruptedAt)} · </span>
            {r.disruptorName} <span className="text-slate">({teamNameById(r.disruptorTeamId)})</span> →{' '}
            {r.victimName} <span className="text-slate">({teamNameById(r.victimTeamId)})</span>
            {r.nodeId && <span className="text-slate"> · {nodeLabelById(r.nodeId)}</span>}
          </span>
          {r.clearedAt == null ? (
            <span className="badge badge--sm badge--red">BLOCKED</span>
          ) : (
            <span className="text-green-dark">
              Cleared {time(r.clearedAt)} by {r.clearedByAdmin ? 'admin' : r.clearedByName ?? 'teammate'}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Every sabotage that fired, newest first: who struck whom, where, and what a
 * Treasure Thief took. Admin-only — players never learn who sabotaged them.
 */
export function SabotageLogPanel({
  records,
  teamNameById,
  nodeLabelById,
}: {
  records: TrlSabotageEffectRecord[];
  teamNameById: (teamId: string) => string;
  nodeLabelById: (nodeId: string) => string;
}) {
  const sorted = [...records].sort((a, b) => b.createdAt - a.createdAt);

  return (
    <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs">
      <h3 className="m-0">Sabotage log</h3>
      {sorted.map((r) => {
        const def = TRL_SABOTAGE_DEFINITIONS[r.type];
        const outcome = describeOutcome(r.payload);
        return (
          <div key={r.id} className="p-2xs rounded-base bg-space bg-o-50 fs-sm">
            <span className="text-slate">{new Date(r.createdAt).toLocaleTimeString()} · </span>
            <img className="trl-sabotage-glyph" src={getTrlSabotageIconUrl(r.type)} alt="" aria-hidden="true" />{' '}
            {def.label}: {teamNameById(r.saboteurTeamId)} → {teamNameById(r.victimTeamId)}
            <span className="text-slate"> · {nodeLabelById(r.nodeId)} · </span>
            {outcome}
          </div>
        );
      })}
    </div>
  );
}

/**
 * What a fired sabotage actually did, per type. Exhaustive on purpose — a new
 * sabotage type should fail to compile here rather than be mislabelled as one of
 * the existing ones.
 */
function describeOutcome(payload: TrlSabotageEffectRecord['payload']): string {
  switch (payload.kind) {
    case 'treasure_thief':
      return payload.stolen.length > 0
        ? `stole ${payload.stolen.map((h) => describeTrlTreasureItem(h.item)).join(', ')}`
        : 'found nothing to steal';
    case 'con_clue':
      return 'fake clue burned out';
    case 'scanner_spy':
      return 'intercepted a screenshot find';
  }
}
